/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "views/LayoutContainers/DashboardLayout";
import DashboardNavbar from "views/Navbars/DashboardNavbar";
import Footer from "views/Footer";
import EventImage from "./components/EventImage";
import EventInfo from "./components/EventInfo";
import Socials from "./components/Socials";
import Address from "./components/Address";
import form from "./schemas/form";
import { useDispatch } from "react-redux";
import { Form, Formik } from "formik";
import validations from "./schemas/validations";
import { useParams } from "react-router-dom";
import { useEventUpdateMutation, useGetEventsByIdQuery } from "../../../../store/api/events";
import { useEffect, useState } from "react";
import checkout from "../new-event/schemas/form";
import { notificationActions } from "../../../../store/notification/notification-slice";

// EditProduct page components

function EditEvent() {
  let { id } = useParams();
  const { data, isLoading, error } = useGetEventsByIdQuery(id);
  const [initialValues, setInitialValues] = useState(null);
  const [statusUpdate] = useEventUpdateMutation();

  const {
    formField: {
      name,
      start_date,
      end_date,
      description,
      event_type,
      will_process,
      cover,
      address,
      city,
      state,
      lat,
      lon,
      website,
      twitter,
      facebook,
      instagram
    }
  } = checkout;

  useEffect(() => {
    if (data) {
      setInitialValues({
        [name.name]: data.name ? data.name : "",
        [start_date.name]: data.start_date ? data.start_date : "",
        [end_date.name]: data.end_date ? data.end_date : "",
        [description.name]: data.description ? data.description : "",
        [event_type.name]: data.event_type,
        [will_process.name]: data.will_process,
        [cover.name]: data.cover,
        [address.name]: data.address ? data.address : "",
        [city.name]: data.city ? data.city : "",
        [state.name]: data.state ? data.state : "",
        [lat.name]: data.lat ? data.lat : 0,
        [lon.name]: data.lon ? data.lon : 0,
        [website.name]: data.website ? data.website : "",
        [twitter.name]: data.twitter ? data.twitter : "",
        [facebook.name]: data.facebook ? data.facebook : "",
        [instagram.name]: data.instagram ? data.instagram : ""
      });
    }
  }, [data]);
  const { formId, formField } = form;
  const dispatch = useDispatch();
  const submitForm = async (values, actions) => {
    dispatch(notificationActions.showLoading(true));
    const save_event = new FormData();
    save_event.append("id", data.id);
    values.name !== data.name && save_event.append("name", values.name);
    values.start_date !== data.start_date && save_event.append("start_date", values.start_date.toISOString());
    values.end_date !== data.end_date && save_event.append("end_date", values.end_date.toISOString());
    values.description !== data.description && save_event.append("description", values.description);
    values.event_type !== data.event_type && save_event.append("event_type", values.event_type);
    values.will_process !== data.will_process && save_event.append("will_process", values.will_process);
    values.cover.name && save_event.append("cover", values.cover);
    values.address !== data.address && save_event.append("address", values.address);
    values.city !== data.city && save_event.append("city", values.city);
    values.state !== data.state && save_event.append("state", values.state);
    values.lat !== data.lat && save_event.append("lat", values.lat);
    values.lon !== data.lon && save_event.append("lon", values.lon);
    values.website !== data.website && save_event.append("website", values.website);
    values.twitter !== data.twitter && save_event.append("twitter", values.twitter);
    values.facebook !== data.facebook && save_event.append("facebook", values.facebook);
    values.instagram !== data.instagram && save_event.append("instagram", values.instagram);
    try {
      const payload = await statusUpdate(save_event).unwrap();
      dispatch(notificationActions.showMessage({ message: "updated successfully", variant: "success" }));
      actions.setSubmitting(false);
      actions.resetForm();
    } catch (e) {
      for (const [key, value] of Object.entries(e.data)) {
        dispatch(notificationActions.showMessage({ message: key + ": " + value, variant: "error" }));
      }
    }
    dispatch(notificationActions.showLoading(false));
  };

  const handleSubmit = (values, actions) => {
    submitForm(values, actions);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {initialValues && <Formik
        initialValues={initialValues}
        validationSchema={validations}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form id={formId} autoComplete="off">
            <MDBox my={3}>
              <MDBox mb={6}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} lg={6}>
                    <MDTypography variant="h4" fontWeight="medium">
                      Edit your event details
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <MDBox display="flex" justifyContent="flex-end">
                      <MDButton disabled={isSubmitting}
                                type="submit" variant="gradient" color="info">
                        save
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                  <EventImage formData={{
                    values,
                    touched,
                    formField,
                    errors,
                    setFieldValue
                  }} initialCover={data.cover} />
                </Grid>
                <Grid item xs={12} lg={8}>
                  <EventInfo formData={{
                    values,
                    touched,
                    formField,
                    errors,
                    setFieldValue
                  }} />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Socials formData={{
                    values,
                    touched,
                    formField,
                    errors,
                    setFieldValue
                  }} />
                </Grid>
                <Grid item xs={12} lg={8}>
                  <Address formData={{
                    values,
                    touched,
                    formField,
                    errors,
                    setFieldValue
                  }} />
                </Grid>
              </Grid>
            </MDBox>
          </Form>
        )}
      </Formik>}
      <Footer />
    </DashboardLayout>
  );
}

export default EditEvent;
